import { useEffect, useState } from 'react'
import { GTR } from '@farewill/ui/tokens'

// the band starts where lib/window/scroll's scrollToDataId lands a section,
// so a section chosen from the nav counts as the one being worked on
const ROOT_MARGIN = `-${GTR.XL} 0px -70% 0px`

const isAtBottomOfScrollablePage = () => {
  const { scrollHeight } = document.documentElement
  const { innerHeight, scrollY } = window

  // a page that doesn't scroll is always at the bottom, which tells us nothing
  return scrollHeight > innerHeight && scrollY + innerHeight >= scrollHeight - 1
}

// ids must be stable between renders, or the sections get re-observed each time
const useActiveSection = (ids: readonly string[]) => {
  const [activeId, setActiveId] = useState(ids[0])

  useEffect(() => {
    const inBand = new Set<string>()

    // the last section can be too short to ever reach the band, so the
    // bottom of the page counts as being in it
    const update = () => {
      const id = isAtBottomOfScrollablePage()
        ? ids[ids.length - 1]
        : ids.find((sectionId) => inBand.has(sectionId))
      if (id) setActiveId(id)
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          const id = target.getAttribute('data-scroll-id')
          if (!id) return
          if (isIntersecting) inBand.add(id)
          else inBand.delete(id)
        })
        update()
      },
      { rootMargin: ROOT_MARGIN }
    )

    ids.forEach((id) => {
      const section = document.querySelector(`[data-scroll-id="${id}"]`)
      if (section) observer.observe(section)
    })

    // at the very top the case header can keep the first section out of the
    // band, and the scroll event arrives before the band catches up with
    // the section just left, so the top of the page counts as the first
    const onScroll = () => {
      if (window.scrollY <= 0) setActiveId(ids[0])
      else update()
    }
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
    }
  }, [ids])

  return { activeId, setActiveId }
}

export default useActiveSection
